/* Metalika Studio - reliability guard. Added 29 Aug 2026.
 *
 * Loaded with defer from <head> (snippets/metalika-fixes.liquid) so it runs
 * before vendors.js and still runs if the Alpine bundle fails entirely.
 * It only ever makes content MORE visible; it never hides anything.
 *
 * Why this exists: the theme hides animated blocks with opacity:0 and relies
 * on Alpine x-intersect observers to reveal them. A block scrolled past before
 * those observers attach never gets revealed and stays invisible for the rest
 * of the session. Three reveal systems are in play, and they disagree:
 *   .anm-fade-element     -> .is-visible on the element
 *   .animate_transition_* -> .active on the element (cards also .lazy_active)
 *   .x-slide-animation    -> .is-ready-to-animate on an ANCESTOR, which then
 *                            runs a keyframe. Inside a content-visibility:auto
 *                            subtree that keyframe can stay pinned at 0%.
 *
 * Strategy: first apply the theme's own classes so real reveal animations
 * still play when the theme is working. Then re-measure, and only for elements
 * still computed at opacity 0 do we pin them visible with an inline style.
 * Surgical, and it cannot suppress a working animation.
 */
(function () {
  'use strict';
  var doc = document;
  var root = doc.documentElement;
  var reduce = false;
  try { reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  var FADE = '.anm-fade-element:not(.is-visible)';
  var CLASSED = '.animate_transition_image,.animate_transition_slide__box,.animate_transition_slide__box-right,.animate_slideshow__box,.animate_transition_card__image';
  var ALL = '.anm-fade-element,.animate_transition_image,.animate_transition_slide__box,.animate_transition_slide__box-right,.animate_slideshow__box,.animate_transition_card__image,.x-slide-animation';

  /* Only the full-bleed hero slideshow (.splide-slideshow) cross-fades one
   * slide at a time. Forcing its inactive slides visible would stack every
   * slide on top of the next. Every other carousel here (.x-splide) shows
   * several slides at once, so those slides must be revealed. */
  function skip(el) {
    if (!el.closest('.splide-slideshow')) return false;
    var slide = el.closest('.splide__slide');
    if (!slide) return false;
    return slide.className.indexOf('is-active') === -1;
  }

  function applyClasses() {
    root.classList.add('mtk-revealed');
    var i, els;
    els = doc.querySelectorAll(FADE);
    for (i = 0; i < els.length; i++) els[i].classList.add('is-visible');

    els = doc.querySelectorAll(CLASSED);
    for (i = 0; i < els.length; i++) {
      if (els[i].classList.contains('active') || skip(els[i])) continue;
      els[i].classList.add('active');
      if (els[i].classList.contains('animate_transition_card__image')) els[i].classList.add('lazy_active');
    }

    els = doc.querySelectorAll('.x-slide-animation');
    for (i = 0; i < els.length; i++) {
      if (skip(els[i])) continue;
      var host = els[i].closest('.shopify-section') || els[i].parentElement;
      if (host && !host.classList.contains('is-ready-to-animate')) host.classList.add('is-ready-to-animate');
    }

    els = doc.querySelectorAll('[x-cloak]');
    for (i = 0; i < els.length; i++) els[i].removeAttribute('x-cloak');
  }

  /* Second pass: anything the theme still has not painted gets pinned. */
  function pinStragglers() {
    var els = doc.querySelectorAll(ALL);
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (skip(el)) continue;
      var cs;
      try { cs = window.getComputedStyle(el); } catch (e) { continue; }
      if (cs.opacity !== '0') continue;
      el.style.setProperty('opacity', '1', 'important');
      el.style.setProperty('transform', 'none', 'important');
      el.style.setProperty('animation', 'none', 'important');
      el.setAttribute('data-mtk-pinned', '1');
    }
  }

  function sweep() {
    try { applyClasses(); } catch (e) {}
    window.setTimeout(function () { try { pinStragglers(); } catch (e) {} }, 900);
  }

  /* Reduced motion: nothing is ever hidden in the first place. */
  if (reduce) {
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', sweep);
    else sweep();
  }

  /* Hard floor: 3s after DOM ready nothing may still be invisible. */
  function armFloor() { window.setTimeout(sweep, 3000); }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', armFloor);
  else armFloor();

  /* Late-streaming sections get the same floor, and so does every subsequent
   * scroll, because content-visibility:auto sections only materialise when
   * they are first approached. */
  window.addEventListener('load', function () { window.setTimeout(sweep, 1200); });

  var pending = false;
  window.addEventListener('scroll', function () {
    if (pending) return;
    pending = true;
    window.setTimeout(function () { pending = false; sweep(); }, 1500);
  }, { passive: true });

  /* ----------------------------------------------------------------- video */
  /* Homepage videos ship with no poster attribute, so the box paints as an
   * empty rectangle until the file decodes. Adopt the nearest still image as
   * the poster, requested at a real size rather than the _small thumbnail.
   *
   * NOTE on preload: an earlier revision of this file forced preload="auto"
   * on the hero video. Measurement showed the hero MP4 is 7.1 MB - about 75%
   * of the entire page weight - so raising its priority competed with the
   * page's own LCP work for no benefit, because the poster already prevents
   * the empty box. The theme's own preload value is left alone.
   */
  function hardenVideos() {
    var vids = doc.querySelectorAll('video');
    for (var i = 0; i < vids.length; i++) {
      var v = vids[i];
      if (v.getAttribute('data-mtk')) continue;
      v.setAttribute('data-mtk', '1');

      if (!v.getAttribute('poster')) {
        var scope = v.closest('.shopify-section, section, div');
        var img = scope ? scope.querySelector('img') : null;
        var src = img && (img.currentSrc || img.getAttribute('src'));
        if (src) {
          /* Shopify serves video preview frames at _small by default, which is
           * a thumbnail upscaled into a full-width slot. Ask for a real size. */
          v.setAttribute('poster', src.replace('_small.', '_1946x.').replace('_100x.', '_1946x.'));
        }
      }

      if (reduce) {
        v.removeAttribute('autoplay');
        v.setAttribute('controls', 'controls');
        try { v.pause(); } catch (e) {}
      }
    }
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', hardenVideos);
  else hardenVideos();
  window.addEventListener('load', hardenVideos);

  /* ------------------------------------------------- featured-collection CTA */
  /* The theme takes the label for a featured collection's "view all" control
   * from a single locale string, so both homepage collections would read
   * "View all products". The mockup gives each section its own words.
   *
   * Rather than fork snippets/featured-collection.liquid (53 KB of core theme
   * code) to add a per-section setting, the two labels are set here. This
   * writes real DOM text, so it reaches the accessibility tree, and the
   * aria-label is updated with it. Only the text node inside .button-text is
   * touched: the button element, its classes and the theme's own button
   * animation are left exactly as rendered. If this script never runs, the
   * theme's own label still shows - generic, but correct.
   */
  var CTA_LABELS = {
    featured_collection_mtk_new: 'All new arrivals',
    featured_collection_mtk_oneofone: 'Shop all one-of-one'
  };

  function labelCollectionCtas() {
    for (var key in CTA_LABELS) {
      if (!Object.prototype.hasOwnProperty.call(CTA_LABELS, key)) continue;
      var section = doc.querySelector('[id$="' + key + '"]');
      if (!section) continue;
      var row = section.querySelector('.featured-collection > .justify-center');
      if (!row) continue;
      var link = row.querySelector('a[aria-label]') || row.querySelector('a');
      if (!link) continue;
      var span = link.querySelector('.button-text');
      if (!span || span.getAttribute('data-mtk-label')) continue;
      span.textContent = CTA_LABELS[key];
      span.setAttribute('data-mtk-label', '1');
      link.setAttribute('aria-label', CTA_LABELS[key]);
    }
  }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', labelCollectionCtas);
  else labelCollectionCtas();
  window.addEventListener('load', labelCollectionCtas);
})();
